import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Modal,
  Divider,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import AddressDialog from '../Home/Address';
import SlotDialog from './SlotDialog';
import FamilyDialog from '../Home/FamilyMembers';

const BookingScreen = () => {
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState('myself');
  const [patientData, setPatientData] = useState({ name: '', gender: '', age: '' });
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [familyDialogOpen, setFamilyDialogOpen] = useState(false);

  const handleSelectPatient = (value) => {
    setSelectedPatient(value);
    if (value === 'myself' && !patientData.name) {
      setFamilyDialogOpen(true); // Open family dialog if "Myself" data is missing
    } else if (value === 'family') {
      setFamilyDialogOpen(true); // Open family dialog for family selection
    }
  };

  return (
    <Box p={3} bgcolor="#F9FAFB" minHeight="100vh">
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Booking Details
      </Typography>

      <Grid container spacing={3}>
        {/* Address Selection */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" mb={1}>
            Address
          </Typography>
          {selectedAddress ? (
            <Box>
              <Typography>
                {`${selectedAddress.line_1}, ${selectedAddress.line_2}, ${selectedAddress.city}, ${selectedAddress.state} - ${selectedAddress.pincode}`}
              </Typography>
              <Button
                variant="text"
                color="primary"
                sx={{ textTransform: 'none', mt: 1 }}
                onClick={() => setAddressModalOpen(true)}
              >
                Change
              </Button>
            </Box>
          ) : (
            <Button
              variant="outlined"
              onClick={() => setAddressModalOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Select or Add Address
            </Button>
          )}
        </Grid>

        {/* Patient Selection */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" mb={1}>
            Patient
          </Typography>
          <RadioGroup
            value={selectedPatient}
            onChange={(e) => handleSelectPatient(e.target.value)}
          >
            <FormControlLabel value="myself" control={<Radio />} label="Myself" />
            <FormControlLabel value="family" control={<Radio />} label="Family Member" />
          </RadioGroup>
          {selectedPatient === 'myself' && patientData.name ? (
            <Box>
              <Typography>{`Name: ${patientData.name}, Age: ${patientData.age}, Gender: ${patientData.gender}`}</Typography>
              <Button
                variant="text"
                color="primary"
                sx={{ textTransform: 'none', mt: 1 }}
                onClick={() => setFamilyDialogOpen(true)}
              >
                Change
              </Button>
            </Box>
          ) : selectedPatient === 'family' && familyMembers.length > 0 ? (
            <Box>
              {familyMembers.map((member, index) => (
                <Typography key={index}>
                  {member.name} ({member.gender}, {member.age} years old)
                </Typography>
              ))}
              <Button
                variant="text"
                color="primary"
                sx={{ textTransform: 'none', mt: 1 }}
                onClick={() => setFamilyDialogOpen(true)}
              >
                Change
              </Button>
            </Box>
          ) : null}
        </Grid>

        {/* Slot Selection */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" mb={1}>
            Slot
          </Typography>
          {selectedSlot ? (
            <Box>
              <Typography>{`Selected Slot: ${selectedSlot}`}</Typography>
              <Button
                variant="text"
                color="primary"
                sx={{ textTransform: 'none', mt: 1 }}
                onClick={() => setSlotDialogOpen(true)}
              >
                Change
              </Button>
            </Box>
          ) : (
            <Button
              variant="outlined"
              onClick={() => setSlotDialogOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Select Slot
            </Button>
          )}
        </Grid>

        {/* Summary Section */}
        <Grid item xs={12}>
          <Box p={2} sx={{ borderRadius: 2, boxShadow: 1, backgroundColor: '#FFFFFF' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Summary
            </Typography>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2" fontWeight="bold">₹1500</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Discount</Typography>
              <Typography variant="body2" color="green" fontWeight="bold">-₹200</Typography>
            </Box>
            <Divider />
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Typography variant="body1" fontWeight="bold">Total</Typography>
              <Typography variant="h6" color="green" fontWeight="bold">₹1300</Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              onClick={() => console.log('Proceeding to Checkout')}
            >
              Proceed to Checkout
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Address Dialog */}
      <AddressDialog
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        onSelect={(address) => {
          setSelectedAddress(address);
          setAddressModalOpen(false);
        }}
      />

      {/* Slot Dialog */}
      <SlotDialog
        open={slotDialogOpen}
        onClose={() => setSlotDialogOpen(false)}
        onSelectSlot={(slot) => setSelectedSlot(slot)}
      />

      {/* Family Dialog */}
      <FamilyDialog
        open={familyDialogOpen}
        onClose={() => setFamilyDialogOpen(false)}
        onSelect={(member) => {
          if (selectedPatient === 'myself') {
            setPatientData(member);
          } else {
            setFamilyMembers((prev) => [...prev, member]);
          }
          setFamilyDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default BookingScreen;